import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Target, ListChecks, Building2, Pencil, Plus, Trash2, FileText, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { api, fmtDate, fileUrl, PROJECT_STATUSES } from "../lib/api";
import { Avatar, Card, HealthPill, PriorityPill, Progress, StatusPill } from "../components/pm/bits";
import Chat from "../components/pm/Chat";
import { QuickStatusButtons } from "../components/pm/StatusActions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const Info = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="flex items-center gap-2 text-xs font-medium text-slate-400"><Icon className="h-4 w-4" /> {label}</div>
    <p className="mt-1.5 text-sm font-bold text-slate-900">{value}</p>
  </div>
);

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { projects, tasks, openTask, openDialog, updateProject, refresh, team } = useApp();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${id}`);
      setP(r.data);
    } catch {
      setErr(true);
    }
  }, [id]);

  // reload when global data changes (task edits etc.)
  useEffect(() => {
    load();
  }, [load, projects, tasks]);

  if (err) return <p className="p-10 text-center text-slate-500">Project not found.</p>;
  if (!p) return <p className="p-10 text-center text-slate-400">Loading project...</p>;

  const pm = team.find((m) => m.id === p.pmId);
  const remove = async () => {
    await api.delete(`/projects/${p.id}`);
    toast.success("Project deleted");
    refresh();
    nav("/projects");
  };

  return (
    <div className="mx-auto max-w-6xl animate-fade-in-up">
      <button onClick={() => nav("/projects")} className="mb-4 flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="back-to-projects">
        <ArrowLeft className="h-4 w-4" /> Back to Projects
      </button>
      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-[#a08450]">{p.code}</p>
            <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-900" data-testid="project-title">{p.name}</h2>
            {p.description && <p className="mt-1 max-w-2xl text-sm text-slate-500">{p.description}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <StatusPill status={p.status} />
              <PriorityPill priority={p.priority} />
              <HealthPill health={p.health} />
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="text-right">
              <p className="text-3xl font-bold text-slate-900">{p.progress}%</p>
              <p className="text-xs text-slate-400">complete</p>
              <div className="mt-2 w-40"><Progress value={p.progress} /></div>
            </div>
            <div className="flex gap-2">
              <Select value={p.status} onValueChange={(v) => updateProject(p.id, { status: v })}>
                <SelectTrigger className="h-9 w-36 text-xs" data-testid="project-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => openDialog("project", p)} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="edit-project-btn">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
            </div>
          </div>
        </div>
      </Card>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Info icon={Calendar} label="Start Date" value={fmtDate(p.startDate)} />
        <Info icon={Target} label="Due Date" value={fmtDate(p.dueDate)} />
        <Info icon={ListChecks} label="Tasks" value={`${p.tasksDone} / ${p.tasksTotal} done`} />
        <Info icon={Building2} label="Customer" value={p.customer} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-900">Tasks ({p.tasks.length})</h3>
              <button onClick={() => openDialog("task", { projectId: p.id })} className="flex items-center gap-1 text-sm font-semibold text-[#00573f] hover:underline" data-testid="add-task-to-project">
                <Plus className="h-4 w-4" /> Add Task
              </button>
            </div>
            <div className="space-y-2">
              {p.tasks.map((t) => (
                <div key={t.id} onClick={() => openTask(t.id)} className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-100 p-3 transition-colors hover:border-[#e7dcc6] hover:bg-[#fbf8f1]" data-testid={`project-task-${t.id}`}>
                  <Avatar person={t.assigneeAvatar} size={30} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-800">{t.title}</p>
                    <p className={`text-xs ${t.overdue ? "text-red-600" : "text-slate-400"}`}>
                      Due {fmtDate(t.dueDate)}{t.phase ? ` · ${t.phase}` : ""}{t.assignee ? ` · ${t.assignee}` : ""}
                    </p>
                  </div>
                  <div className="hidden sm:block"><PriorityPill priority={t.priority} /></div>
                  <StatusPill status={t.status} />
                  <QuickStatusButtons task={t} />
                </div>
              ))}
              {p.tasks.length === 0 && <p className="text-sm text-slate-400">No tasks yet.</p>}
            </div>
          </Card>

          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-900">Documents ({p.documents.length})</h3>
              <button onClick={() => openDialog("document", { projectId: p.id })} className="flex items-center gap-1 text-sm font-semibold text-[#00573f] hover:underline" data-testid="add-doc-to-project">
                <Plus className="h-4 w-4" /> Add Document
              </button>
            </div>
            {p.documents.length === 0 ? (
              <p className="text-sm text-slate-400">No documents for this project.</p>
            ) : (
              <div className="space-y-2">
                {p.documents.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 rounded-xl border border-slate-100 p-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#f5efe3] text-[#a08450]"><FileText className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-800">{d.name}</p>
                      <p className="truncate text-xs text-slate-400">{d.category} · {d.fileName || d.url || "No file"}</p>
                    </div>
                    {d.storagePath && <a href={fileUrl(d.id)} target="_blank" rel="noreferrer" className="rounded-md p-2 text-slate-500 hover:bg-slate-100" title="Download"><Download className="h-4 w-4" /></a>}
                    {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="rounded-md p-2 text-slate-500 hover:bg-slate-100" title="Open link"><ExternalLink className="h-4 w-4" /></a>}
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-6">
            <Chat scope="project" refId={p.id} title="Project Discussion" />
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="mb-4 text-base font-bold text-slate-900">Project Manager</h3>
            <div className="flex items-center gap-3">
              <Avatar person={p.pmAvatar} size={44} />
              <div className="min-w-0">
                <p className="truncate font-semibold text-slate-900">{p.pm || "Unassigned"}</p>
                <p className="truncate text-xs text-slate-400">{pm?.role || "Project Manager"}</p>
                {pm?.email && <a href={`mailto:${pm.email}`} className="truncate text-xs text-[#00573f] hover:underline">{pm.email}</a>}
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="mb-4 text-base font-bold text-slate-900">Summary</h3>
            <div className="space-y-2.5 text-sm">
              {[
                ["Open tasks", p.tasks.filter((t) => !["Completed", "Canceled"].includes(t.status)).length],
                ["Completed", p.tasksDone],
                ["Overdue", p.overdueTasks],
                ["Documents", p.docsCount],
                ["Milestones", p.tasks.filter((t) => t.milestone).length],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between"><span className="text-slate-500">{l}</span><span className={`font-bold ${l === "Overdue" && v ? "text-red-600" : "text-slate-900"}`}>{v}</span></div>
              ))}
            </div>
          </Card>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-red-700" data-testid="delete-project-btn"><Trash2 className="h-4 w-4" /> Delete project</button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {p.name}?</AlertDialogTitle>
                <AlertDialogDescription>This removes the project and all {p.tasksTotal} of its tasks.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}
